import React, { useMemo } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, TimeScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler, ChartData, ChartOptions, TooltipItem
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import { SystemStat, Timeframe, MetricType, ViewMode } from './SystemHistory';
import { useCurrency } from '../../../../hooks/useCurrency';

ChartJS.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface Props {
  data: SystemStat[];
  range: Timeframe;
  metric: MetricType;
  viewMode: ViewMode;
}

const SystemHistoryChart: React.FC<Props> = ({ data, range, metric, viewMode }) => {
  const { currencySymbol, formatAmountWithoutCurrency, convertFromUSD } = useCurrency();

  const bounds = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    switch (range) {
      case '24h': start.setHours(end.getHours() - 24); break;
      case '7d': start.setDate(end.getDate() - 7); break;
      case '30d': start.setDate(end.getDate() - 30); break;
      case '3M': start.setMonth(end.getMonth() - 3); break;
      case '6M': start.setMonth(end.getMonth() - 6); break;
      case '1Y': start.setFullYear(end.getFullYear() - 1); break;
      case 'ALL': return { min: undefined, max: undefined };
    }
    start.setHours(0, 0, 0, 0);
    return { min: start.getTime(), max: end.getTime() };
  }, [range]);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
  }, [data]);

  const chartData: ChartData<'line'> = useMemo(() => {
    let color = '#3b82f6';
    if (metric === 'WITHDRAWS') color = '#ef4444';
    if (metric === 'DEPOSITS') color = '#10b981';
    if (metric === 'USERS') {
      if (viewMode === 'NEW_USERS') color = '#3b82f6';
      else if (viewMode === 'CONCURRENT') color = '#f97316';
      else color = '#a855f7';
    }
    if (metric === 'NEWSLETTER') color = '#eab308';

    const getVal = (d: SystemStat) => {
      if (viewMode === 'MONEY') return d.amount;
      if (viewMode === 'NEW_USERS') return d.amount;
      if (viewMode === 'CONCURRENT') return d.concurrent || 0;
      return d.count;
    };

    return {
      datasets: [
        {
          label: metric,
          data: sortedData.map(d => ({ x: new Date(d.day).getTime(), y: getVal(d) })),
          borderColor: color,
          backgroundColor: `${color}22`,
          borderWidth: 2,
          fill: true,
          tension: 0,
          pointRadius: sortedData.length === 1 ? 5 : 2,
          pointHoverRadius: 6,
          pointBackgroundColor: color,
          pointBorderColor: '#171717',
          pointBorderWidth: 1,
          spanGaps: true
        }
      ],
    };
  }, [sortedData, viewMode, metric]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        min: bounds.min,
        max: bounds.max,
        adapters: { date: { locale: de } },
        grid: { color: '#262626', drawTicks: false },
        time: {
          unit: range === '24h' ? 'hour' : 'day',
          displayFormats: {
            hour: 'HH:mm',
            day: 'dd.MM.'
          },
          tooltipFormat: 'dd.MM.yyyy HH:mm'
        },
        ticks: {
          color: '#525252',
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#262626' },
        ticks: {
          color: '#525252',
          font: { size: 10 },
          // Force integer values for the scale
          precision: 0,
          stepSize: viewMode === 'MONEY' ? undefined : 1,
          callback: (val) => {
            const num = typeof val === 'number' ? val : 0;
            // Only format as money if viewMode is MONEY, else return integer
            return viewMode === 'MONEY' ? `${currencySymbol}${num}` : num;
          }
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#171717',
        borderColor: '#262626',
        borderWidth: 1,
        callbacks: {
          label: (ctx: TooltipItem<'line'>) => {
            const val = ctx.parsed.y ?? 0;
            if (viewMode === 'MONEY') {
              const converted = convertFromUSD(val);
              return `Value: ${currencySymbol}${formatAmountWithoutCurrency(converted)}`;
            }
            return `Amount: ${val}`;
          }
        }
      }
    }
  };

  return <Chart type="line" data={chartData} options={options} />;
};

export default SystemHistoryChart;